import { useForm } from "@tanstack/react-form";
import { useEffect, useId, useMemo, useState } from "react";

import { t } from "../../../i18n";
import { categorySchema } from "../../../lib/schemas";
import { trpc } from "../../../lib/trpc";
import { fieldError } from "../../../lib/validation";
import {
  Button,
  Dialog,
  Field,
  FieldMessage,
  Icon,
  Input,
  Stack,
  Switch,
  TapTarget,
} from "../../ui";
import { ChipsInput } from "../components/ChipsInput";
import { CATEGORY_COLOR_CHOICES, CATEGORY_ICON_CHOICES } from "../constants";
import type { CategoryItem } from "../utils";

interface CategoryFormDialogProps {
  mode: "create" | "edit";
  householdId: string;
  category?: CategoryItem | null;
  open: boolean;
  onClose: () => void;
}

export function CategoryFormDialog({
  mode,
  householdId,
  category,
  open,
  onClose,
}: CategoryFormDialogProps) {
  const utils = trpc.useUtils();
  const create = trpc.household.addCategory.useMutation({
    onSuccess: () => {
      utils.household.get.invalidate({ groupId: householdId });
      onClose();
    },
  });
  const update = trpc.household.updateCategory.useMutation({
    onSuccess: () => {
      utils.household.get.invalidate({ groupId: householdId });
      onClose();
    },
  });

  const [patternDraft, setPatternDraft] = useState("");

  // useMemo so the object reference is stable across renders. Otherwise
  // the useEffect below would treat every render as a "new initialValues"
  // and reset the form — losing whatever the user has typed.
  const initialValues = useMemo(
    () =>
      mode === "edit" && category
        ? {
            name: category.name,
            icon: (category.icon ?? "tag") as string,
            color: category.color,
            defaultShared: category.defaultShared,
            matchPatterns: [...category.matchPatterns],
          }
        : {
            name: "",
            icon: "tag" as string,
            color: CATEGORY_COLOR_CHOICES[0] ?? "#6366f1",
            defaultShared: true,
            matchPatterns: [] as string[],
          },
    [mode, category],
  );

  const form = useForm({
    defaultValues: initialValues,
    validators: { onChange: categorySchema, onSubmit: categorySchema },
    onSubmit: ({ value }) => {
      // Flush any draft chip the user typed but didn't commit with Enter.
      const trimmedDraft = patternDraft.trim();
      const merged =
        trimmedDraft &&
        !value.matchPatterns.some((p) => p.toUpperCase() === trimmedDraft.toUpperCase())
          ? [...value.matchPatterns, trimmedDraft]
          : value.matchPatterns;
      const payload = { ...value, name: value.name.trim(), matchPatterns: merged };
      if (mode === "create") {
        create.mutate({ groupId: householdId, category: payload });
      } else if (category) {
        update.mutate({ groupId: householdId, categoryId: category.id, category: payload });
      }
    },
  });

  useEffect(() => {
    if (!open) return;
    form.reset(initialValues);
    setPatternDraft("");
  }, [open, initialValues, form]);

  const addPattern = () => {
    const next = patternDraft.trim();
    if (!next) return;
    const current = form.state.values.matchPatterns;
    if (current.some((p) => p.toUpperCase() === next.toUpperCase())) {
      setPatternDraft("");
      return;
    }
    form.setFieldValue("matchPatterns", [...current, next]);
    setPatternDraft("");
  };
  const removePattern = (pattern: string) =>
    form.setFieldValue(
      "matchPatterns",
      form.state.values.matchPatterns.filter((p) => p !== pattern),
    );

  const mutation = mode === "create" ? create : update;
  const formId = useId();

  const dialogStrings =
    mode === "create" ? t.household.categories.createDialog : t.household.categories.editDialog;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={dialogStrings.title}
      description={dialogStrings.description}
      footer={
        <>
          <Button onClick={onClose}>{t.common.cancel}</Button>
          <Button type="submit" form={formId} variant="primary" isLoading={mutation.isPending}>
            {mode === "create" ? t.common.create : t.common.save}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <Stack gap={4}>
          <form.Field name="name">
            {(field) => (
              <Field
                label={t.household.categories.createDialog.nameLabel}
                required
                error={fieldError(field)}
              >
                <Input
                  autoFocus
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t.household.categories.createDialog.namePlaceholder}
                  invalid={!!fieldError(field)}
                />
              </Field>
            )}
          </form.Field>
          <form.Field name="icon">
            {(field) => (
              <Field label={t.household.categories.createDialog.iconLabel}>
                <div className="flex max-h-[8.5rem] flex-wrap gap-2 overflow-y-auto py-0.5 pr-1 sm:max-h-[14rem]">
                  {CATEGORY_ICON_CHOICES.map((iconName) => (
                    <TapTarget
                      key={iconName}
                      onClick={() => field.handleChange(iconName)}
                      aria-pressed={field.state.value === iconName}
                      className={`flex h-9 w-9 items-center justify-center rounded-md border ${
                        field.state.value === iconName
                          ? "border-accent-500 text-ink-900 ring-2 ring-accent-200"
                          : "border-ink-200 text-ink-600 hover:border-ink-300"
                      }`}
                    >
                      <Icon name={iconName} size="sm" />
                    </TapTarget>
                  ))}
                </div>
              </Field>
            )}
          </form.Field>
          <form.Field name="color">
            {(field) => (
              <Field label={t.household.categories.createDialog.colorLabel}>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_COLOR_CHOICES.map((c) => (
                    <TapTarget
                      key={c}
                      onClick={() => field.handleChange(c)}
                      aria-pressed={field.state.value === c}
                      className={`h-8 w-8 rounded-md border ${
                        field.state.value === c
                          ? "border-transparent ring-2 ring-ink-900 ring-offset-2"
                          : "border-ink-200"
                      }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </Field>
            )}
          </form.Field>
          <form.Field name="defaultShared">
            {(field) => (
              <Switch
                checked={field.state.value}
                onCheckedChange={(v) => field.handleChange(v)}
                label={t.household.categories.createDialog.sharedLabel}
                hint={t.household.categories.createDialog.sharedHint}
              />
            )}
          </form.Field>
          <form.Field name="matchPatterns">
            {(field) => (
              <Field
                label={t.household.categories.createDialog.patternsLabel}
                hint={t.household.categories.patternHint}
              >
                <ChipsInput
                  chips={field.state.value}
                  draft={patternDraft}
                  onDraftChange={setPatternDraft}
                  onAdd={addPattern}
                  onRemove={removePattern}
                  onRemoveLast={() => field.handleChange(field.state.value.slice(0, -1))}
                  placeholder={
                    field.state.value.length === 0
                      ? t.household.categories.createDialog.patternsPlaceholder
                      : t.household.categories.addPatternPlaceholder
                  }
                  addButtonLabel={t.household.categories.addPatternButton}
                  removeLabel={t.household.categories.removePatternLabel}
                />
              </Field>
            )}
          </form.Field>
          {mutation.isError && (
            <FieldMessage tone="error" className="text-sm">
              {mutation.error.message}
            </FieldMessage>
          )}
        </Stack>
      </form>
    </Dialog>
  );
}
